import { MedplumClient } from "@medplum/core";

import { pdf } from "@react-pdf/renderer";

import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { SNOMEDCodes } from "@/fhir/enums/snomed";
import { Systems } from "@/fhir/enums/systems";
import { HealthGorillaLabOrderInternalStatus } from "@/health-gorilla/types/labs";
import {
  getOrganizationReference,
  getResourcesFromBundle,
  joinReferenceString,
  joinTokenString,
} from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import { fetchAllPaginatedResources } from "@/utils/pagination";

import { ExportDataResult } from "../types/export";
import { mergeExportWithAttachments } from "../utils/pdf-merge";

const PAGE_SIZE = 20;

export function getLabOrdersQueryForExport(clientId: string, practiceId?: string) {
  const category = joinTokenString(Systems.SNOMED, SNOMEDCodes.LaboratoryProcedure);
  const needsReviewStatus = joinTokenString(
    QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
    HealthGorillaLabOrderInternalStatus.NeedsReview,
  );
  const reviewedStatus = joinTokenString(
    QuiltedHealthCodeSystems.HealthGorillaLabOrderInternalStatus,
    HealthGorillaLabOrderInternalStatus.Reviewed,
  );
  return {
    subject: joinReferenceString("Patient", clientId),
    _filter: `category eq ${category} and (category eq ${needsReviewStatus} or category eq ${reviewedStatus})`,
    "status:not": "revoked",
    ...(practiceId ? { _compartment: getOrganizationReference(practiceId) } : {}),
  };
}

export async function fetchAllLabOrdersForExport(
  medplum: MedplumClient,
  clientId: string,
  practiceId?: string,
) {
  const result = await fetchAllPaginatedResources(medplum, "ServiceRequest", {
    ...getLabOrdersQueryForExport(clientId, practiceId),
    _elements: "id",
  });

  if (!result.success) {
    showSystemErrorNotification();
    return [];
  }

  return result.data.map((labOrder) => labOrder.id).filter((id): id is string => Boolean(id));
}

export async function fetchLabOrdersForExport(
  medplum: MedplumClient,
  clientId: string,
  page: number,
  practiceId?: string,
) {
  const { bundle } = await medplum.searchResources("ServiceRequest", {
    ...getLabOrdersQueryForExport(clientId, practiceId),
    _sort: "-_lastUpdated",
    _count: PAGE_SIZE,
    _offset: (page - 1) * PAGE_SIZE,
    _total: "accurate",
  });

  return {
    data: getResourcesFromBundle("ServiceRequest", bundle).map((labOrder) => ({ labOrder })),
    total: Math.ceil((bundle.total ?? 0) / PAGE_SIZE),
  };
}
export async function fetchHealthGorillaMergedPDFBlob(
  medplum: MedplumClient,
  pdfDocument: JSX.Element,
  exportData: ExportDataResult,
) {
  // Generate the base export PDF from the React component
  const exportPdfBlob = await pdf(pdfDocument).toBlob();
  const exportPdfBytes = await exportPdfBlob.arrayBuffer();

  const hasAttachments =
    exportData.labReportAttachments.length > 0 || exportData.fileAttachments.length > 0;
  let finalPdfBytes: Uint8Array;

  if (hasAttachments) {
    const mergeResult = await mergeExportWithAttachments(
      medplum,
      exportPdfBytes,
      exportData.labReportAttachments,
      exportData.fileAttachments,
    );

    if (mergeResult.success && mergeResult.mergedPdfBytes) {
      finalPdfBytes = mergeResult.mergedPdfBytes;
    } else {
      finalPdfBytes = new Uint8Array(exportPdfBytes);
    }
  } else {
    finalPdfBytes = new Uint8Array(exportPdfBytes);
  }

  // Create blob URL for the PDF viewer
  const pdfBlob = new Blob([finalPdfBytes.buffer as ArrayBuffer], {
    type: "application/pdf",
  });
  return URL.createObjectURL(pdfBlob);
}
